import { useCallback, useEffect, useState, type ReactNode } from "react";

import type { JourneyView } from "../ride-journey.ts";
import type { RiderApi } from "../rider-api.ts";
import { SCOPES } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { MapHero } from "./Hero.tsx";
import { Note, PhoneShell } from "./PhoneShell.tsx";
import { StateScreen } from "./StateScreen.tsx";

/**
 * `4 Confirming` — the designed screen for the moment nobody knows yet.
 *
 * The measurement calls this one of only two **buildable** screens in the rider's booking flow, and
 * it is the most interesting screen in the file: it is the design drawing an unconfirmed state
 * deliberately, in its own vocabulary, rather than drawing a spinner over a guess. The chip is
 * `unknown` — the dashed pill with a literal `?` — and the note is the dashed `pending` outline
 * saying _"If the connection drops we'll check what happened instead of booking twice."_
 *
 * That sentence is a contract with the rider, and this screen keeps it two ways:
 *
 *   - **The key is the quote's.** `Quote.tsx` books with `ride-request:<quoteId>`, so a retry after
 *     a dropped connection is the same booking rather than a second one. Document 06 refuses a key
 *     reused with materially different terms rather than answering from the first request, so the
 *     one case this could go wrong is visible as a 409 rather than silent as a duplicate ride.
 *   - **Checking is a read.** When the address names a ride request, this screen **reads** EXP-002
 *     rather than re-posting. `CLAUDE.md` section 2: _"A 'check status' action is a read, never a
 *     retry of the command."_
 *
 * ## Two states, and the second is the one the design does not draw
 *
 * While the booking is in flight the address carries a quote and no ride, and the screen is the
 * design's, word for word. Once MOB-03 has answered, the address carries a ride request — and the
 * design has no screen for "it worked", because its next screen is `5 Finding`. So the confirmed
 * state says the booking is in and offers the step forward; its words are this build's and are
 * flagged as such.
 *
 * `5 Finding` **used to be unbuildable**, and this screen used to say so. DSP-04 published
 * `process.status`, `process.attempt`, `process.maximumAttempts`, `process.deadlineAt` and
 * `process.failureReason` only inside the 202 of an event-ingestion `POST`, behind
 * `dispatch:orchestrate`, so the confirmed state's note told a rider that how the search was going
 * was not published. ADR 0031 served
 * `GET /v1/tenants/{tenantId}/dispatch-processes/{demandId}` under `dispatch:read` and made that
 * sentence false, so `Follow your ride` now opens `5 Finding` and the note says what it actually
 * does. EXP-002's own aggregate still answers `dispatch: null` with
 * `dispatchAbsence: no_published_route` — that field is stale upstream and is recorded in
 * `docs/design/DESIGN_RECONCILIATION.md`, not worked around here.
 */

export interface ConfirmingProps {
  readonly api: RiderApi;
  readonly tenantId: string;
  readonly customerId: string;
  /** The ride request, once MOB-03 has answered. Absent while the booking is in flight. */
  readonly rideRequestId: string | undefined;
  readonly tripId: string | undefined;
  /** Opens `5 Finding` for this ride, which is the design's own next screen. */
  readonly onFollowRide: () => void;
  readonly onBack: () => void;
}

export function Confirming(props: ConfirmingProps): ReactNode {
  const { api, tenantId, customerId, rideRequestId, tripId } = props;
  const [view, setView] = useState<JourneyView | undefined>(undefined);
  const [state, setState] = useState<ViewState>({ kind: "loading" });

  const check = useCallback(() => {
    if (rideRequestId === undefined) return;
    setState({ kind: "loading" });
    api
      .rideJourney({
        tenantId,
        customerId,
        rideRequestId,
        ...(tripId === undefined ? {} : { tripId }),
      })
      .then((answer) => {
        setView(answer);
        setState({ kind: "ready" });
      })
      .catch((error: unknown) => setState(stateForError(error, [SCOPES.journey])));
  }, [api, tenantId, customerId, rideRequestId, tripId]);

  useEffect(check, [check]);

  /*
   * In flight: the design's screen, exactly as drawn.
   *
   * No read is made and none is possible — MOB-03 has not answered, so there is no identifier to
   * read by. This is the state the design is actually about.
   */
  if (rideRequestId === undefined) {
    return (
      <PhoneShell
        screen="journey"
        hero={<MapHero kind="route" />}
        back={{ glyph: "arrow_back", label: "Back", onPress: props.onBack }}
        safety={{ armed: false }}
        chip={{ class: "unknown", label: "not confirmed yet" }}
        title="Booking your ride…"
        sub="Please keep the app open for a moment."
        actions={[]}
      >
        <Note
          kind="pending"
          text="If the connection drops we'll check what happened instead of booking twice."
        />
      </PhoneShell>
    );
  }

  if (state.kind !== "ready" || view === undefined) {
    return (
      <StateScreen
        screen="journey"
        state={state}
        onRetry={check}
        purpose="Checking whether your booking went through."
      />
    );
  }

  /*
   * MOB-03 answered and the demand was cancelled.
   *
   * Reachable when a rider books, loses signal, and comes back after cancelling elsewhere — and the
   * one case where "Booking your ride…" would be a lie about something that definitely happened.
   */
  if (view.requestStatus === "cancelled") {
    return (
      <PhoneShell
        screen="journey"
        hero={<MapHero kind="route" />}
        back={{ glyph: "arrow_back", label: "Back", onPress: props.onBack }}
        safety={{ armed: false }}
        chip={{ class: "closed", label: "cancelled" }}
        title="This booking was cancelled"
        sub={
          view.cancellation === null
            ? "Nothing was charged."
            : `Cancelled ${view.cancellation.cancelledAt} · ${view.cancellation.reasonCode}. Nothing was charged.`
        }
        actions={[
          { label: "Get a new price", glyph: "refresh", primary: true, onPress: props.onBack },
        ]}
        flag="wording not in the design"
      />
    );
  }

  /*
   * MOB-03 could not be read at all, which is not the same as "not confirmed yet".
   *
   * `requestStatus: null` means the ride request layer did not answer or answered about somebody
   * else's demand, and EXP-002 drops it rather than reporting it. Drawing the design's
   * "Booking your ride…" over that would tell a rider their booking is in flight when the truth is
   * that this application cannot see it — and the right next move for the two is different.
   */
  if (view.requestStatus === null) {
    return (
      <PhoneShell
        screen="journey"
        hero={<MapHero kind="route" />}
        back={{ glyph: "arrow_back", label: "Back", onPress: props.onBack }}
        safety={{ armed: false }}
        chip={{ class: "unknown", label: "unknown" }}
        title="We can't tell whether this booked"
        sub="Your booking couldn't be read just now. Nothing was booked twice — checking is a read, and this app never re-sends a booking to find out."
        actions={[{ label: "Check again", glyph: "refresh", primary: true, onPress: check }]}
        flag="wording not in the design"
      />
    );
  }

  return (
    <PhoneShell
      screen="journey"
      hero={<MapHero kind="route" />}
      back={{ glyph: "arrow_back", label: "Back", onPress: props.onBack }}
      safety={{ armed: false }}
      /*
       * `requested` — MOB-03's own status word, in the design's own `idle` pill.
       *
       * Not `done`, and not a tick. The booking exists; the ride does not yet. MOB-03's status
       * vocabulary is `requested | cancelled` and **nothing moves it when a trip starts**, so this
       * word will still read `requested` for the whole ride and for ever afterwards — which is why
       * every later screen reads `stage` rather than this.
       */
      chip={{ class: "idle", label: "requested" }}
      title="Your ride is booked"
      sub="We're asking drivers near you now."
      flag="wording not in the design"
      actions={[
        {
          label: "Follow your ride",
          glyph: "arrow_forward",
          primary: true,
          onPress: props.onFollowRide,
        },
      ]}
    >
      <Note
        kind="info"
        /*
         * What this note used to say, and why it no longer says it.
         *
         * It read: _"How the search is going isn't published, so this app can't show it counting
         * down."_ That was true of every build of this application until ADR 0031 served DSP-04's
         * dispatch process under `dispatch:read`, and it was the recorded reason `5 Finding` and
         * `5b No driver` were absent. It is now false, and a screen that kept saying it would be
         * telling a rider the application cannot do the thing the next tap does.
         */
        text="Next you'll see the search counting down, and your driver as soon as one is assigned."
      />
    </PhoneShell>
  );
}
